import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckIcon from '@material-ui/icons/Check';
import UndoIcon from '@material-ui/icons/Undo';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import FilterListIcon from '@material-ui/icons/FilterList';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Chip from '@material-ui/core/Chip';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Collapse from '@material-ui/core/Collapse';
import InputAdornment from '@material-ui/core/InputAdornment';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import RepeatIcon from '@material-ui/icons/Repeat';
import StopIcon from '@material-ui/icons/Stop';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import TableChartIcon from '@material-ui/icons/TableChart';
import Tooltip from '@material-ui/core/Tooltip';
import CommentIcon from '@material-ui/icons/Comment';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import WarningIcon from '@material-ui/icons/Warning';
import TodayIcon from '@material-ui/icons/Today';
import EventIcon from '@material-ui/icons/Event';
import ArchiveIcon from '@material-ui/icons/Archive';
import UnarchiveIcon from '@material-ui/icons/Unarchive';
import Badge from '@material-ui/core/Badge';
import Box from '@material-ui/core/Box';
import LockIcon from '@material-ui/icons/Lock';
import PublicIcon from '@material-ui/icons/Public';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import CircularProgress from '@material-ui/core/CircularProgress';
import { format, isToday, isTomorrow, parseISO, isPast } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import { AuthContext } from '../../context/Auth/AuthContext';
import taskService from '../../services/taskService';
import taskRecurrenceService from '../../services/taskRecurrence';
import TaskFormModal from '../../components/TaskFormModal';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '1rem',
    padding: '1rem',
    [theme.breakpoints.down('sm')]: {
      margin: '0.5rem',
      padding: '0.5rem'
    }
  },
  listContainer: {
    width: '100%',
    maxWidth: '800px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: '8px',
    boxShadow: theme.shadows[2]
  },
  taskItem: {
    marginBottom: '8px',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  completedTask: {
    opacity: 0.6,
    textDecoration: 'line-through'
  },
  archivedTask: {
    opacity: 0.7,
    backgroundColor: (theme.palette.type === 'dark' || theme.palette.mode === 'dark') 
      ? theme.palette.grey[700] 
      : theme.palette.grey[100],
    borderLeft: `4px solid #795548`
  },
  priorityChip: {
    marginRight: '8px'
  },
  categoryChip: {
    marginRight: '8px',
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.secondary.contrastText
  },
  statusChip: {
    marginRight: '8px'
  },
  dragHandle: {
    cursor: 'grab',
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.primary.main
    }
  },
  dragging: {
    boxShadow: theme.shadows[8],
    transform: 'rotate(5deg)',
    opacity: 0.9
  },
  mobileContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      gap: '2rem',
      maxWidth: '1400px',
      alignItems: 'flex-start'
    }
  },
  sidebar: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    [theme.breakpoints.up('md')]: {
      width: '350px'
    }
  },
  mainContent: {
    flex: 1,
    width: '100%'
  },
  mobileTitle: {
    fontSize: '1.5rem',
    textAlign: 'center',
    width: '100%',
    marginBottom: '1rem',
    [theme.breakpoints.up('md')]: {
      fontSize: '2.125rem',
      marginBottom: '2rem'
    }
  },
  mobilePaper: {
    padding: '1rem',
    [theme.breakpoints.up('md')]: {
      padding: '1.5rem'
    }
  },
  mobileButtonGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row'
    }
  },
  taskItemMobile: {
    marginBottom: '8px',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    },
    [theme.breakpoints.down('sm')]: {
      padding: '8px'
    }
  },
  taskTextMobile: {
    paddingRight: 128,
    wordBreak: 'break-word',
    [theme.breakpoints.up('md')]: {
      paddingRight: 160
    },
    [theme.breakpoints.down('sm')]: {
      '& .MuiListItemText-primary': {
        fontSize: '0.9rem'
      },
      '& .MuiListItemText-secondary': {
        fontSize: '0.8rem'
      }
    }
  },
  mobileActions: {
    display: 'flex',
    flexDirection: 'row',
    gap: '4px',
  minWidth: 120,
  justifyContent: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: '2px',
      '& .MuiIconButton-root': {
        padding: '6px'
      }
    }
  },
  chipContainer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4px',
    [theme.breakpoints.down('sm')]: {
      gap: '2px',
      '& .MuiChip-root': {
        height: '20px',
        fontSize: '0.7rem'
      }
    }
  },
  descriptionClamp: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    whiteSpace: 'normal'
  },
  taskIconsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
    '& .MuiBadge-root': {
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'scale(1.1)'
      }
    }
  },
  commentIcon: {
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.primary.dark
    }
  },
  attachmentIcon: {
    color: theme.palette.secondary.main,
    '&:hover': {
      color: theme.palette.secondary.dark
    }
  },
  subtaskIcon: {
    color: theme.palette.warning.main,
    '&:hover': {
      color: theme.palette.warning.dark
    }
  },
  subtaskBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.warning.contrastText,
      fontWeight: 600,
      fontSize: '0.75rem',
      right: -8,
      top: 8
    }
  },
  commentBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      fontWeight: 600,
      fontSize: '0.75rem',
      right: -8,
      top: 8
    }
  },
  attachmentBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.secondary.contrastText,
      fontWeight: 600,
      fontSize: '0.75rem',
      right: -8,
      top: 8
    }
  },
  taskSection: {
    marginBottom: theme.spacing(3),
    '&:last-child': {
      marginBottom: 0
    }
  },
  sectionHeader: {
    padding: theme.spacing(2),
    backgroundColor: (theme.palette.type === 'dark' || theme.palette.mode === 'dark') 
      ? theme.palette.grey[800] 
      : theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    position: 'sticky',
    top: 0,
    zIndex: 1,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5)
    }
  },
  sectionIcon: {
    fontSize: '1.2rem'
  },
  privateIcon: {
    color: theme.palette.warning.main
  },
  publicIcon: {
    color: theme.palette.success.main
  },
  visibilityChip: {
    marginLeft: '8px',
    height: '20px',
    fontSize: '0.7rem'
  },
  privateChip: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
    '& .MuiChip-icon': {
      color: theme.palette.warning.dark
    }
  },
  publicChip: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
    '& .MuiChip-icon': {
      color: theme.palette.success.dark
    }
  }
}));

const TaskArchive = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [filters, setFilters] = useState({
    searchParam: '',
    status: '',
    priority: '',
    category: '',
    dueDateFrom: '',
    dueDateTo: '',
    sortBy: 'createdAt',
    sortOrder: 'DESC'
  });
  
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [totalTasks, setTotalTasks] = useState(0);
  const [taskCounts, setTaskCounts] = useState({});
  
  // Estados para o modal unificado
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [editingTaskData, setEditingTaskData] = useState(null);

  // Estado para modal de confirmação de desarquivamento
  const [unarchiveConfirmModal, setUnarchiveConfirmModal] = useState({
    open: false,
    taskId: null,
    taskTitle: ''
  });

  // Carregar tarefas arquivadas ao montar o componente
  useEffect(() => {
    loadArchivedTasks();
  }, [filters]);

  // Carregar contadores
  useEffect(() => {
    if (tasks.length > 0) {
      loadTaskCounts();
    }
  }, [tasks]);

  const loadArchivedTasks = async () => {
    try {
      setLoading(true);
      
      // Preparar parâmetros de filtro + forçar apenas arquivadas
      const params = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {});
      
      // Forçar apenas tarefas arquivadas
      params.isArchived = true;
      
      const response = await taskService.getAll(params);
      setTasks(response.tasks || []);
      setTotalTasks(response.count || 0);
    } catch (error) {
      console.error('Erro ao carregar tarefas arquivadas:', error);
      toast.error('Erro ao carregar tarefas arquivadas');
    } finally {
      setLoading(false);
    }
  };

  const loadTaskCounts = async () => {
    try {
      const counts = {};
      for (const task of tasks) {
        try {
          const [commentsRes, attachmentsRes, subtasksRes] = await Promise.all([
            taskService.getComments(task.id).catch(() => ({ comments: [] })),
            taskService.getAttachments(task.id).catch(() => []),
            taskService.getSubtasks(task.id).catch(() => ({ subtasks: [] }))
          ]);
          
          counts[task.id] = {
            comments: commentsRes.comments?.length || 0,
            attachments: attachmentsRes.attachments?.length || attachmentsRes?.length || 0,
            subtasks: subtasksRes.subtasks?.length || 0
          };
        } catch (error) {
          console.log(`Erro ao carregar contadores para tarefa ${task.id}:`, error);
          counts[task.id] = { comments: 0, attachments: 0, subtasks: 0 };
        }
      }
      setTaskCounts(counts);
    } catch (error) {
      console.error('Erro ao carregar contadores:', error);
    }
  };

  // Função para separar tarefas em públicas e privadas
  const getSeparatedTasks = () => {
    const privateTasks = tasks.filter(task => !task.isPublic);
    const publicTasks = tasks.filter(task => task.isPublic);
    
    return {
      privateTasks,
      publicTasks,
      totalPrivate: privateTasks.length,
      totalPublic: publicTasks.length
    };
  };

  // Função para renderizar uma seção de tarefas
  const renderTaskSection = (tasks, sectionTitle, isPublic, startIndex = 0) => {
    if (tasks.length === 0) return null;

    return (
      <div className={classes.taskSection} key={sectionTitle}>
        <div className={classes.sectionHeader}>
          {isPublic ? (
            <PublicIcon className={`${classes.sectionIcon} ${classes.publicIcon}`} />
          ) : (
            <LockIcon className={`${classes.sectionIcon} ${classes.privateIcon}`} />
          )}
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {sectionTitle} ({tasks.length})
          </Typography>
        </div>
        
        <List>
          {tasks.map((task, index) => (
            <Draggable 
              key={task.id} 
              draggableId={task.id.toString()} 
              index={startIndex + index}
            >
              {(provided, snapshot) => (
                <ListItem 
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  className={`${classes.taskItemMobile} ${task.status === 'completed' ? classes.completedTask : ''} ${classes.archivedTask} ${snapshot.isDragging ? classes.dragging : ''}`}
                >
                  <ListItemIcon {...provided.dragHandleProps}>
                    <DragIndicatorIcon className={classes.dragHandle} />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.taskTextMobile}
                    primary={
                      <div className={classes.chipContainer}>
                        <span style={{ marginRight: '8px', fontWeight: 500 }}>{task.title}</span>
                        <Chip
                          label={getPriorityLabel(task.priority)}
                          color={getPriorityColor(task.priority)}
                          size="small"
                          className={classes.priorityChip}
                        />
                        <Chip
                          label={task.status === 'completed' ? 'Concluída' : 'Pendente'}
                          color={task.status === 'completed' ? 'default' : 'primary'}
                          size="small"
                          variant={task.status === 'completed' ? 'outlined' : 'default'}
                          className={classes.statusChip}
                        />
                        <Chip
                          icon={task.isPublic ? <PublicIcon /> : <LockIcon />}
                          label={task.isPublic ? 'Pública' : 'Privada'}
                          size="small"
                          className={`${classes.visibilityChip} ${task.isPublic ? classes.publicChip : classes.privateChip}`}
                        />
                        <Chip
                          icon={<ArchiveIcon />}
                          label="Arquivada"
                          size="small"
                          style={{
                            backgroundColor: '#795548',
                            color: '#fff'
                          }}
                        />
                        {getTaskAlertChip(task)}
                        {task.category && (
                          <Chip
                            label={task.category}
                            size="small"
                            className={classes.categoryChip}
                          />
                        )}
                        {task.isRecurring && (
                          <Chip
                            icon={<RepeatIcon />}
                            label="Recorrente"
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        )}
                      </div>
                    }
                    secondary={
                      <div>
                        {task.description && (
                          <div className={classes.descriptionClamp} title={task.description}>
                            {task.description}
                          </div>
                        )}
                        <div style={{ fontSize: '0.75rem', marginTop: '4px' }}>
                          Criado: {new Date(task.createdAt).toLocaleString()}
                          {task.dueDate && (
                            <span> | Vencimento: {new Date(task.dueDate).toLocaleDateString()}</span>
                          )}
                          <span> | Arquivado: {new Date(task.updatedAt).toLocaleString()}</span>
                        </div>
                        {/* Contadores de funcionalidades */}
                        <Box className={classes.taskIconsContainer}>
                          {taskCounts[task.id]?.comments > 0 && (
                            <Tooltip title={`${taskCounts[task.id].comments} comentário(s)`} arrow>
                              <Badge 
                                badgeContent={taskCounts[task.id].comments} 
                                className={classes.commentBadge}
                                overlap="circular"
                              >
                                <CommentIcon 
                                  fontSize="small" 
                                  className={classes.commentIcon}
                                />
                              </Badge>
                            </Tooltip>
                          )}
                          {taskCounts[task.id]?.attachments > 0 && (
                            <Tooltip title={`${taskCounts[task.id].attachments} anexo(s)`} arrow>
                              <Badge 
                                badgeContent={taskCounts[task.id].attachments} 
                                className={classes.attachmentBadge}
                                overlap="circular"
                              >
                                <AttachFileIcon 
                                  fontSize="small" 
                                  className={classes.attachmentIcon}
                                />
                              </Badge>
                            </Tooltip>
                          )}
                          {taskCounts[task.id]?.subtasks > 0 && (
                            <Tooltip title={`${taskCounts[task.id].subtasks} subtarefa(s)`} arrow>
                              <Badge 
                                badgeContent={taskCounts[task.id].subtasks} 
                                className={classes.subtaskBadge}
                                overlap="circular"
                              >
                                <AccountTreeIcon 
                                  fontSize="small" 
                                  className={classes.subtaskIcon}
                                />
                              </Badge>
                            </Tooltip>
                          )}
                        </Box>
                      </div>
                    }
                  />
                  <ListItemSecondaryAction>
                    <div className={classes.mobileActions}>
                      <IconButton 
                        onClick={() => handleToggleStatus(task)}
                        disabled={loading}
                        title={task.status === 'completed' ? 'Marcar como pendente' : 'Marcar como concluída'}
                        size="small"
                      >
                        {task.status === 'completed' ? <UndoIcon /> : <CheckIcon />}
                      </IconButton>
                      <IconButton 
                        onClick={() => handleOpenFormModal(task)}
                        disabled={loading}
                        title="Abrir tarefa (visualizar/editar tudo)"
                        size="small"
                      >
                        <OpenInNewIcon />
                      </IconButton>
                      {task.isRecurring && (
                        <IconButton 
                          onClick={() => handleStopRecurrence(task.id)}
                          disabled={loading}
                          title="Parar recorrência"
                          size="small"
                        >
                          <StopIcon />
                        </IconButton>
                      )}
                      <IconButton 
                        onClick={() => handleUnarchiveTask(task.id)}
                        disabled={loading}
                        title="Desarquivar tarefa"
                        size="small"
                      >
                        <UnarchiveIcon />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDelete(task.id)}
                        disabled={loading}
                        title="Excluir tarefa"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  </ListItemSecondaryAction>
                </ListItem>
              )}
            </Draggable>
          ))}
        </List>
      </div>
    );
  };

  const getTaskAlertChip = (task) => {
    if (!task.dueDate) return null;
    
    const dueDate = parseISO(task.dueDate);
    
    if (isPast(dueDate) && task.status !== 'completed') {
      return <Chip size="small" icon={<WarningIcon />} label="Atrasada" color="secondary" />;
    }
    if (isToday(dueDate)) {
      return <Chip size="small" icon={<TodayIcon />} label="Hoje" color="primary" />;
    }
    if (isTomorrow(dueDate)) {
      return <Chip size="small" icon={<EventIcon />} label="Amanhã" />;
    }
    
    return null;
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      searchParam: '',
      status: '',
      priority: '',
      category: '',
      dueDateFrom: '',
      dueDateTo: '',
      sortBy: 'createdAt',
      sortOrder: 'DESC'
    });
  };

  const getActiveFiltersCount = () => {
    return Object.entries(filters).filter(([key, value]) => {
      if (key === 'sortBy' && value === 'createdAt') return false;
      if (key === 'sortOrder' && value === 'DESC') return false;
      return value && value !== '';
    }).length;
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta tarefa?')) {
      return;
    }

    try {
      setLoading(true);
      await taskService.delete(taskId);
      toast.success('Tarefa excluída com sucesso!');
      await loadArchivedTasks();
    } catch (error) {
      console.error('Erro ao excluir tarefa:', error);
      toast.error('Erro ao excluir tarefa');
    } finally {
      setLoading(false);
    }
  };

  const handleStopRecurrence = async (taskId) => {
    if (!window.confirm('Tem certeza que deseja parar a recorrência desta tarefa?')) {
      return;
    }

    try {
      setLoading(true);
      await taskRecurrenceService.stopRecurrence(taskId);
      toast.success('Recorrência parada com sucesso!');
      await loadArchivedTasks();
    } catch (error) {
      console.error('Erro ao parar recorrência:', error);
      toast.error('Erro ao parar recorrência');
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchiveTask = async (taskId) => {
    // Encontrar a tarefa para obter o título
    const task = tasks.find(t => t.id === taskId);
    
    // Abrir modal de confirmação de desarquivamento
    setUnarchiveConfirmModal({
      open: true,
      taskId: taskId,
      taskTitle: task?.title || 'Tarefa'
    });
  };

  const handleConfirmUnarchive = async () => {
    const { taskId } = unarchiveConfirmModal;
    
    try {
      setLoading(true);
      await taskService.update(taskId, { isArchived: false });
      toast.success('Tarefa desarquivada com sucesso!');
      await loadArchivedTasks();
      
      // Fechar modal de confirmação
      setUnarchiveConfirmModal({ open: false, taskId: null, taskTitle: '' });
      
    } catch (error) {
      console.error('Erro ao desarquivar tarefa:', error);
      toast.error('Erro ao desarquivar tarefa');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelUnarchive = () => {
    setUnarchiveConfirmModal({ open: false, taskId: null, taskTitle: '' });
  };

  const handleToggleStatus = async (task) => {
    try {
      setLoading(true);
      const newStatus = task.status === 'pending' ? 'completed' : 'pending';
      await taskService.update(task.id, { status: newStatus });
      toast.success(`Tarefa ${newStatus === 'completed' ? 'concluída' : 'reaberta'}!`);
      await loadArchivedTasks();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      toast.error('Erro ao alterar status da tarefa');
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'secondary';
      case 'medium': return 'primary';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high': return 'Alta';
      case 'medium': return 'Média';
      case 'low': return 'Baixa';
      default: return priority;
    }
  };

  const handleExportPDF = async () => {
    try {
      setLoading(true);
      await taskService.exportPDF({ ...filters, isArchived: true });
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      toast.error('Erro ao exportar PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      await taskService.exportExcel({ ...filters, isArchived: true });
      toast.success('Excel exportado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      toast.error('Erro ao exportar Excel');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenFormModal = async (task = null) => {
    if (task && task.id) {
      try {
        setLoading(true);
        
        const taskData = await taskService.getById(task.id);
        const subtasks = await taskService.getSubtasks(task.id);
        const comments = await taskService.getComments(task.id);
        const attachments = await taskService.getAttachments(task.id);
        
        const completeTaskData = {
          ...taskData,
          subtasks: subtasks.subtasks || [],
          comments: comments.comments || [],
          attachments: attachments.attachments || attachments || []
        };
        
        setEditingTaskData(completeTaskData);
      } catch (error) {
        console.error('Erro ao carregar dados da tarefa:', error);
        toast.error('Erro ao carregar dados da tarefa');
        setEditingTaskData(task);
      } finally {
        setLoading(false);
      }
    } else {
      setEditingTaskData(task);
    }
    setFormModalOpen(true);
  };

  const handleCloseFormModal = () => {
    setFormModalOpen(false);
    setEditingTaskData(null);
  };

  const handleSaveTaskFromModal = async (taskData) => {
    try {
      const finalTaskData = {
        ...taskData,
        userId: user.id,
        companyId: user.companyId,
        dueDate: taskData.dueDate && taskData.dueDate !== '' ? taskData.dueDate : null
      };

      const { subtasks, comments, attachments, ...taskMainData } = finalTaskData;

      if (editingTaskData) {
        await taskService.update(editingTaskData.id, taskMainData);
        toast.success('Tarefa atualizada com sucesso!');
      }

      await loadArchivedTasks();
      handleCloseFormModal();
      
    } catch (error) {
      console.error('Erro ao salvar tarefa:', error);
      toast.error('Erro ao salvar tarefa');
    }
  };

  return (
    <div className={classes.root}>
      {/* Título Principal */}
      <Typography variant="h4" component="h1" className={classes.mobileTitle}>
        📦 Tarefas Arquivadas
      </Typography>

      {/* Layout Responsivo */}
      <div className={classes.mobileContainer}>
        
        {/* Sidebar - Controles */}
        <div className={classes.sidebar}>
          
          {/* Botão Voltar */}
          <Paper className={classes.mobilePaper}>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push('/tasks')}
              fullWidth
              style={{ marginBottom: '1rem' }}
            >
              Voltar às Tarefas
            </Button>
            
            {/* Botões de Exportação */}
            <div className={classes.mobileButtonGroup}>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={handleExportPDF}
                disabled={loading}
                style={{ flex: 1 }}
              >
                PDF
              </Button>
              <Button
                variant="outlined"
                startIcon={<TableChartIcon />}
                onClick={handleExportExcel}
                disabled={loading}
                style={{ flex: 1 }}
              >
                Excel
              </Button>
            </div>
          </Paper>

          {/* Filtros */}
          <Paper className={classes.mobilePaper}>
            <Typography variant="h6" gutterBottom>
              🔍 Filtros
            </Typography>
            
            <TextField
              label="Buscar tarefas arquivadas..."
              value={filters.searchParam}
              onChange={(e) => handleFilterChange('searchParam', e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              style={{ marginBottom: '1rem' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: filters.searchParam && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => handleFilterChange('searchParam', '')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
              size="small"
              fullWidth
              style={{ marginBottom: '1rem' }}
            >
              Filtros {getActiveFiltersCount() > 0 && `(${getActiveFiltersCount()})`}
            </Button>
            
            {getActiveFiltersCount() > 0 && (
              <Button
                variant="text"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                size="small"
                color="secondary"
                fullWidth
                style={{ marginBottom: '1rem' }}
              >
                Limpar Filtros
              </Button>
            )}
            
            <Collapse in={showFilters}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="pending">Pendente</MenuItem>
                    <MenuItem value="completed">Concluída</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel>Prioridade</InputLabel>
                  <Select
                    value={filters.priority}
                    onChange={(e) => handleFilterChange('priority', e.target.value)}
                    label="Prioridade"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    <MenuItem value="low">Baixa</MenuItem>
                    <MenuItem value="medium">Média</MenuItem>
                    <MenuItem value="high">Alta</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  label="Categoria"
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                />
                
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel>Ordenar por</InputLabel>
                  <Select
                    value={filters.sortBy}
                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                    label="Ordenar por"
                  >
                    <MenuItem value="createdAt">Data de criação</MenuItem>
                    <MenuItem value="updatedAt">Data de arquivamento</MenuItem>
                    <MenuItem value="dueDate">Data de vencimento</MenuItem>
                    <MenuItem value="priority">Prioridade</MenuItem>
                    <MenuItem value="status">Status</MenuItem>
                    <MenuItem value="title">Título</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel>Ordem</InputLabel>
                  <Select
                    value={filters.sortOrder}
                    onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                    label="Ordem"
                  >
                    <MenuItem value="DESC">Decrescente</MenuItem>
                    <MenuItem value="ASC">Crescente</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </Collapse>
          </Paper>
        </div>

        {/* Conteúdo Principal - Lista de Tarefas Arquivadas */}
        <div className={classes.mainContent}>
          <Paper className={classes.listContainer}>
            <Typography variant="h6" style={{ padding: '1rem' }}>
              📦 Tarefas Arquivadas ({totalTasks})
            </Typography>
            
            {loading ? (
              <Typography style={{ padding: '1rem', textAlign: 'center' }}>
                Carregando tarefas arquivadas...
              </Typography>
            ) : tasks.length === 0 ? (
              <Typography style={{ padding: '1rem', textAlign: 'center' }}>
                Nenhuma tarefa arquivada encontrada
              </Typography>
            ) : (
              <DragDropContext onDragEnd={() => {}}>
                <Droppable droppableId="archived-tasks">
                  {(provided) => {
                    const { privateTasks, publicTasks, totalPrivate, totalPublic } = getSeparatedTasks();
                    
                    return (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                      >
                        {/* Seção de Tarefas Privadas Arquivadas */}
                        {renderTaskSection(privateTasks, "Tarefas Privadas Arquivadas", false, 0)}
                        
                        {/* Seção de Tarefas Públicas Arquivadas */}
                        {renderTaskSection(publicTasks, "Tarefas Públicas Arquivadas", true, totalPrivate)}
                        
                        {privateTasks.length === 0 && publicTasks.length === 0 && (
                          <Typography style={{ padding: '1rem', textAlign: 'center' }}>
                            Nenhuma tarefa arquivada encontrada com os filtros aplicados
                          </Typography>
                        )}
                        
                        {provided.placeholder}
                      </div>
                    );
                  }}
                </Droppable>
              </DragDropContext>
            )}
          </Paper>
        </div>
      </div>

      {/* Modal de Formulário (Visualização/Edição) */}
      <TaskFormModal
        open={formModalOpen}
        onClose={handleCloseFormModal}
        onSave={handleSaveTaskFromModal}
        task={editingTaskData}
      />

      {/* Modal de Confirmação de Desarquivamento */}
      <Dialog
        open={unarchiveConfirmModal.open}
        onClose={handleCancelUnarchive}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          style={{
            background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
            color: '#fff',
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: '1.2rem'
          }}
        >
          📤 Desarquivar Tarefa
        </DialogTitle>
        <DialogContent
          style={{
            textAlign: 'center',
            padding: '24px',
            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(69, 160, 73, 0.05) 100%)'
          }}
        >
          <Typography 
            variant="h6" 
            style={{ 
              marginBottom: '16px',
              color: '#2e7d32',
              fontWeight: 500
            }}
          >
            📤 Tem certeza que deseja desarquivar?
          </Typography>
          <Paper
            elevation={1}
            style={{
              padding: '16px',
              backgroundColor: '#f1f8e9',
              border: '1px solid #c8e6c9',
              borderRadius: '8px'
            }}
          >
            <Typography 
              variant="body1" 
              style={{ 
                fontWeight: 'bold',
                color: '#1b5e20'
              }}
            >
              📋 "{unarchiveConfirmModal.taskTitle}"
            </Typography>
          </Paper>
          <Typography 
            variant="body2" 
            style={{ 
              marginTop: '16px',
              color: '#2e7d32',
              fontStyle: 'italic'
            }}
          >
            A tarefa voltará à lista ativa
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '16px', justifyContent: 'center', gap: '12px' }}>
          <Button
            onClick={handleCancelUnarchive}
            color="default"
            variant="outlined"
            style={{
              minWidth: '120px',
              borderColor: '#757575',
              color: '#757575'
            }}
            disabled={loading}
          >
            ❌ Cancelar
          </Button>
          <Button
            onClick={handleConfirmUnarchive}
            color="primary"
            variant="contained"
            style={{
              minWidth: '120px',
              background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
              color: '#fff',
              fontWeight: 'bold'
            }}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? 'Desarquivando...' : '📤 Desarquivar'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TaskArchive;
